import React, { useState, useEffect } from 'react';
import * as M from '@material-ui/core';
import { Add, Delete, Edit, Search, Clear } from '@material-ui/icons';
import { api } from '@services/api';
import { ToastMessage } from '@components/ToastMessage';
import { type Color } from '@material-ui/lab';

interface Risco {
  id_risco: string;
  nome: string;  // ← API retorna "nome", não "nome_risco"
}

export function Riscos() {
  const [riscos, setRiscos] = useState<Risco[]>([]);
  const [riscosFiltrados, setRiscosFiltrados] = useState<Risco[]>([]);
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageSend, setMessageSend] = useState({
    cod: '',
    message: '',
    severity: '' as Color,
    show: false
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [riscoToDelete, setRiscoToDelete] = useState<Risco | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [riscoToEdit, setRiscoToEdit] = useState<Risco | null>(null);
  const [editCodigo, setEditCodigo] = useState('');
  const [editNome, setEditNome] = useState('');

  useEffect(() => {
    fetchRiscos();
  }, []);

  const filtrarRiscos = (termo: string) => {
    if (!termo.trim()) {
      setRiscosFiltrados(riscos);
      setPage(0);
      return;
    }

    const termoLowerCase = termo.toLowerCase().trim();
    const filtrados = riscos.filter(
      (risco) =>
        risco.id_risco.toLowerCase().includes(termoLowerCase) ||
        risco.nome.toLowerCase().includes(termoLowerCase)
    );
    setRiscosFiltrados(filtrados);
    setPage(0);
  };

  const handleSearch = () => {
    filtrarRiscos(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setRiscosFiltrados(riscos);
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  async function fetchRiscos() {
    setIsLoading(true);
    try {
      const res = await api.get('/ravim/riscos');
      if (res.status === 200) {
        setRiscos(res.data);
        setRiscosFiltrados(res.data);
      }
    } catch (error: any) {
      setMessageSend({
        cod: error.code,
        message: error.message,
        severity: 'error',
        show: true
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function addRisco() {
    if (!codigo.trim() || !nome.trim()) {
      setMessageSend({
        cod: '400',
        message: 'Preencha todos os campos!',
        severity: 'warning',
        show: true
      });
      return;
    }

    const data = {
      id_risco: codigo,
      nome: nome  // ← API espera "nome"
    };

    setIsLoading(true);
    try {
      const res = await api.post('/ravim/cad-riscos', data);
      if (res.status === 200) {
        setMessageSend({
          cod: String(res.status),
          message: res.data.message,
          severity: 'success',
          show: true
        });
        setCodigo('');
        setNome('');
        fetchRiscos();
      }
    } catch (error: any) {
      setMessageSend({
        cod: error.code,
        message: error.response?.data?.message || error.message,
        severity: 'error',
        show: true
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteClick = (risco: Risco) => {
    setRiscoToDelete(risco);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRiscoToDelete(null);
  };

  async function confirmDeleteRisco() {
    if (riscoToDelete) {
      setIsLoading(true);
      try {
        const res = await api.delete(`/ravim/riscos/${riscoToDelete.id_risco}`);
        if (res.status === 200) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'success',
            show: true
          });
          fetchRiscos();
        }
      } catch (error: any) {
        setMessageSend({
          cod: error.code,
          message: error.response?.data?.message || error.message,
          severity: 'error',
          show: true
        });
      } finally {
        setIsLoading(false);
        handleCloseDeleteDialog();
      }
    }
  }

  const handleEditClick = (risco: Risco) => {
    setRiscoToEdit(risco);
    setEditCodigo(risco.id_risco);
    setEditNome(risco.nome);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setRiscoToEdit(null);
    setEditCodigo('');
    setEditNome('');
  };

  async function saveEditedRisco() {
    if (riscoToEdit) {
      const data = {
        id_risco: editCodigo,
        nome: editNome  // ← API espera "nome"
      };
      setIsLoading(true);
      try {
        const res = await api.put(`/ravim/riscos/${riscoToEdit.id_risco}`, data);
        if (res.status === 200) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'success',
            show: true
          });
          fetchRiscos();
        }
      } catch (error: any) {
        setMessageSend({
          cod: error.code,
          message: error.response?.data?.message || error.message,
          severity: 'error',
          show: true
        });
      } finally {
        setIsLoading(false);
        handleCloseEditDialog();
      }
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const dadosExibidos = riscosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <M.Container maxWidth="lg" style={{ padding: '20px' }}>
      <M.Box mb={4}>
        <M.Typography
          style={{ textAlign: 'center', fontWeight: 'bold' }}
          variant="h4"
          component="h1"
          color="primary"
        >
          Gerenciamento de Riscos
        </M.Typography>
      </M.Box>

      <M.Grid container spacing={3}>
        {/* Seção de Cadastro */}
        <M.Grid item xs={12} md={4}>
          <M.Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
            <M.Typography variant="h6" gutterBottom>Cadastrar Novo Risco</M.Typography>
            <M.Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              <M.TextField
                onChange={(e) => setCodigo(e.target.value)}
                label="Código do Risco"
                variant="outlined"
                value={codigo}
                fullWidth
                size="small"
                placeholder="Ex: 01.02.001"
              />
              <M.TextField
                label="Nome do Risco"
                variant="outlined"
                onChange={(e) => setNome(e.target.value)}
                value={nome}
                fullWidth
                size="small"
                placeholder="Ex: Asbestos (ou amianto)"
              />
              <M.Button
                startIcon={<Add />}
                onClick={addRisco}
                disabled={!(codigo.length > 0 && nome.length > 0)}
                variant="contained"
                color="primary"
                fullWidth
              >
                {isLoading ? 'Cadastrando...' : 'Cadastrar'}
              </M.Button>
            </M.Box>
          </M.Paper>
        </M.Grid>

        {/* Seção de Listagem */}
        <M.Grid item xs={12} md={8}>
          <M.Paper elevation={3} style={{ padding: '20px' }}>
            <M.Typography variant="h6" gutterBottom>Riscos Cadastrados</M.Typography>
            
            {/* Barra de Pesquisa */}
            <M.Box display="flex" alignItems="center" style={{ gap: 2 }} mb={2}>
              <M.TextField
                label="Pesquisar por código ou nome"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                placeholder="Digite o código ou nome do risco..."
                style={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <M.InputAdornment position="start">
                      <Search />
                    </M.InputAdornment>
                  ),
                }}
              />
              <M.Button
                onClick={handleSearch}
                variant="contained"
                color="primary"
                startIcon={<Search />}
              >
                Buscar
              </M.Button>
              {searchTerm && (
                <M.Button
                  onClick={handleClearSearch}
                  variant="outlined"
                  color="secondary"
                  startIcon={<Clear />}
                >
                  Limpar
                </M.Button>
              )}
            </M.Box>

            {searchTerm && (
              <M.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Encontrado(s) {riscosFiltrados.length} resultado(s) para "{searchTerm}"
              </M.Typography>
            )}

            {isLoading ? (
              <M.Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <M.CircularProgress />
              </M.Box>
            ) : riscosFiltrados.length === 0 ? (
              <M.Typography>
                {searchTerm ? 'Nenhum risco encontrado com o termo pesquisado.' : 'Nenhum risco cadastrado.'}
              </M.Typography>
            ) : (
              <>
                <M.TableContainer>
                  <M.Table>
                    <M.TableHead>
                      <M.TableRow>
                        <M.TableCell style={{ fontWeight: 'bold' }}>Código</M.TableCell>
                        <M.TableCell style={{ fontWeight: 'bold' }}>Nome do Risco</M.TableCell>
                        <M.TableCell align="right" style={{ fontWeight: 'bold' }}>Ações</M.TableCell>
                      </M.TableRow>
                    </M.TableHead>
                    <M.TableBody>
                      {dadosExibidos.map((risco) => (
                        <M.TableRow key={risco.id_risco} hover>
                          <M.TableCell>{risco.id_risco}</M.TableCell>
                          <M.TableCell>{risco.nome}</M.TableCell>
                          <M.TableCell align="right">
                            <M.IconButton
                              aria-label="editar"
                              onClick={() => handleEditClick(risco)}
                              color="primary"
                              size="small"
                            >
                              <Edit />
                            </M.IconButton>
                            <M.IconButton
                              aria-label="excluir"
                              onClick={() => handleDeleteClick(risco)}
                              color="secondary"
                              size="small"
                            >
                              <Delete />
                            </M.IconButton>
                          </M.TableCell>
                        </M.TableRow>
                      ))}
                    </M.TableBody>
                  </M.Table>
                </M.TableContainer>
                <M.TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={riscosFiltrados.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </>
            )}
          </M.Paper>
        </M.Grid>
      </M.Grid>

      {/* Modal de Confirmação de Exclusão */}
      <M.Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <M.DialogTitle>Confirmar Exclusão</M.DialogTitle>
        <M.DialogContent>
          <M.DialogContentText>
            Tem certeza que deseja excluir o risco "{riscoToDelete?.nome}" (Código: {riscoToDelete?.id_risco})?
          </M.DialogContentText>
        </M.DialogContent>
        <M.DialogActions>
          <M.Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </M.Button>
          <M.Button onClick={confirmDeleteRisco} color="secondary">
            Excluir
          </M.Button>
        </M.DialogActions>
      </M.Dialog>

      {/* Modal de Edição */}
      <M.Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <M.DialogTitle>Editar Risco</M.DialogTitle>
        <M.DialogContent>
          <M.TextField
            margin="dense"
            label="Código do Risco"
            type="text"
            fullWidth
            value={editCodigo}
            onChange={(e) => setEditCodigo(e.target.value)}
            disabled
          />
          <M.TextField
            margin="dense"
            label="Nome do Risco"
            type="text"
            fullWidth
            value={editNome}
            onChange={(e) => setEditNome(e.target.value)}
          />
        </M.DialogContent>
        <M.DialogActions>
          <M.Button onClick={handleCloseEditDialog} color="primary">
            Cancelar
          </M.Button>
          <M.Button onClick={saveEditedRisco} color="primary">
            Salvar
          </M.Button>
        </M.DialogActions>
      </M.Dialog>

      <ToastMessage
        code={messageSend.cod}
        message={messageSend.message}
        onClose={() =>
          setMessageSend({
            cod: messageSend.cod,
            message: messageSend.message,
            severity: messageSend.severity,
            show: false
          })
        }
        show={messageSend.show}
        severity={messageSend.severity}
      />
    </M.Container>
  );
}